import React, {useEffect, useState} from 'react';
import Icon from "./Icon";
import Alert from "./Alert";
import {PageLoader} from "./Loader";
import {Button} from "./Button";
import Modal from "./Modal";
import {CheckboxInputField} from "./FormField";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
    createUserAlbum,
    deleteUserAlbum,
    fetchUserAlbum, updateUserAlbum,
    USER_ALBUM_KEY,
    USER_ALBUMS_KEY,
} from "../../api/public-voissa/users";
import {toastError, toastSuccess} from "../../libs/toast";
import {ALBUM} from "../../const/toasts";


const initialAlbum = {
    title: '',
    description: '',
    right: "guest",
    order : "-createdAt",
    comment: {
        allowed: true,
        toValidate: false
    }
};

const AlbumInformations = ({
        albumId = null,
        userId = null,
        handleClose = () =>{}
    }) =>{
    const queryClient = useQueryClient();

    const [currentAlbum, setCurrentAlbum] = useState(initialAlbum);
    const [toggleAlert, setToggleAlert] = useState(false);

    const {isSuccess : isSuccessAlbum, data : dataAlbum, isError : isErrorAlbum} = useQuery(
        [USER_ALBUM_KEY, albumId],
        ()=> fetchUserAlbum(userId, albumId),
        {
            staleTime : 0,
            enabled : !!albumId && !!userId
        }
    );

    const {
        mutate : mutateDeleteAlbum,
    } = useMutation(() => deleteUserAlbum(userId, albumId),{
        onSuccess: async () =>{
            await queryClient.invalidateQueries([USER_ALBUMS_KEY, userId]);
            toastSuccess(ALBUM.SUCCESS.DELETE);
            handleClose();
        },
        onError: () =>{
            toastError(ALBUM.ERROR.DELETE);
        }
    });

    const {
        mutate : mutateUpdateAlbum,
    } = useMutation(() => updateUserAlbum(userId, albumId, currentAlbum),{
        onSuccess: async () =>{
            await queryClient.invalidateQueries([USER_ALBUMS_KEY, userId]);
            toastSuccess(ALBUM.SUCCESS.UPDATE);
            handleClose();
        },
        onError: () =>{
            toastError(ALBUM.ERROR.UPDATE);
        }
    });

    const {
        mutate : mutateCreateAlbum,
    } = useMutation(() => createUserAlbum(userId, currentAlbum),{
        onSuccess: async () =>{
            await queryClient.invalidateQueries([USER_ALBUMS_KEY, userId]);
            toastSuccess(ALBUM.SUCCESS.CREATE);
            handleClose();
        },
        onError: () =>{
            toastError(ALBUM.ERROR.CREATE);
        }
    });

    function handleSubmitAlbum() {
        if(isSuccessAlbum){
            mutateUpdateAlbum();
        }
        else{
            mutateCreateAlbum();
        }
    }

    useEffect(() => {
        const handleData = () => {
            setCurrentAlbum(dataAlbum || initialAlbum);
        }
        handleData();
        return () => {
            setCurrentAlbum(initialAlbum)
        };
    }, [dataAlbum]);

    if(isErrorAlbum){
        return (<div id={"albumInformations"} className={"modal-container"}>
            Impossible de charger votre album
        </div>)
    }

    return !(!!albumId || !isSuccessAlbum) ? (
        <div id={"albumInformations"} className={"modal-container"}>
            <PageLoader/>
        </div>
    ) : (
        <div id={"albumInformations"} className={"modal-container"}>
            <div className={"modal-topline"}>
                {isSuccessAlbum ? (
                    <div className={"album-name"}>
                        Editer une collection : <b>{dataAlbum.title}</b>
                    </div>
                ) : (
                    <div className={"album-name"}>
                    Créer une collection
                    </div>
                )}

                <span onClick={() => handleClose()}><Icon type={"close"}/></span>
            </div>
            <div className={`album-datas-wrapper modal-body`}>
                <div className={"informations"}>
                    <div className={"category-title"}><Icon type={"pencil"}/> Informations</div>
                    <input type={"text"}
                           className={`${currentAlbum.title.length < 3 ? 'error' : ''}`}
                           placeholder={"Titre"} name={"title"}
                           onChange={(event) => setCurrentAlbum({...currentAlbum, title : event.target.value})}
                           value={currentAlbum.title || ""}/>
                    {currentAlbum.title.length < 3 ? <div className={"error-text"}>Vous devez remplir un titre pour votre album.</div> : null}
                    <textarea placeholder={"Description"} name={"description"}
                              onChange={(event) => setCurrentAlbum({...currentAlbum, description : event.target.value})}
                              value={currentAlbum.description || ""}/>
                </div>
                <div className={"tag-wrapper"}>
                    <div className={"category-title"}><Icon type={"cog"}/> Options</div>
                    <ul className={`filter ${currentAlbum.right.length === 0 ? 'error' : ''}`}>
                        <li onClick={() => setCurrentAlbum({...currentAlbum, right : 'guest'})}
                            className={currentAlbum.right === "guest" ? "active" : ""}>🌐 Public
                        </li>
                        <li onClick={() => setCurrentAlbum({...currentAlbum, right : 'public'})}
                            className={currentAlbum.right === "public" ? "active" : ""}>👥 Membres
                        </li>
                        <li onClick={() => setCurrentAlbum({...currentAlbum, right : 'friends'})}
                            className={currentAlbum.right === "friends" ? "active" : ""}>⭕ Mon cercle
                        </li>
                        <li onClick={() => setCurrentAlbum({...currentAlbum, right : 'certified'})}
                            className={currentAlbum.right === "certified" ? "active" : ""}>✅ Certifiés
                        </li>
                        <li onClick={() => setCurrentAlbum({...currentAlbum, right : 'private'})}
                            className={currentAlbum.right === "private" ? "active" : ""}>🔒 Privé
                        </li>
                    </ul>
                    <div className={`temperature-information`}>
                        {currentAlbum.right === "guest" && (
                            "🌐 Public : La collection n’a pas de restriction, tout le monde peut le voir."
                        )}
                        {currentAlbum.right === "public" && (
                            "👥 Membres : La collection n’est visible que pour les membres inscrits sur Voissa."
                        )}
                        {currentAlbum.right === "friends" && (
                            "⭕ Mon cercle : Seuls les membres de mon cercle pourront voir le contenu de la collection"
                        )}
                        {currentAlbum.right === "certified" && (
                            "✅ Certifiés : Seulement les membres certifiés pourront voir le contenu de la collection"
                        )}
                        {currentAlbum.right === "private" && (
                            "🔒 Privé : Masquera votre collection aux autres membres à l'exception des modérateurs et administrateurs."
                        )}
                    </div>
                    <div className={"category-title mt-8"}>Trier vos medias par</div>
                    <ul className={`filter`}>
                        <li onClick={() => setCurrentAlbum({...currentAlbum, order : 'createdAt'})}
                            className={currentAlbum.order === "createdAt" ? "active" : ""}>Date croissante
                        </li>
                        <li onClick={() => setCurrentAlbum({...currentAlbum, order : '-createdAt'})}
                            className={currentAlbum.order === "-createdAt" ? "active" : ""}>Date décroissante
                        </li>
                        <li onClick={() => setCurrentAlbum({...currentAlbum, order : 'title'})}
                            className={currentAlbum.order === "title" ? "active" : ""}>Titre
                        </li>
                    </ul>
                    <div className={"album-options-checkbox"}>
                        <CheckboxInputField id="allowed"
                                            htmlFor="allowed"
                                            label={"Permettre aux membres de laisser des commentaires"}
                                            onChange={() => setCurrentAlbum({...currentAlbum, comment : {...currentAlbum.comment, 'allowed' : !currentAlbum.comment.allowed}})}
                                            checked={currentAlbum && currentAlbum.comment && currentAlbum.comment.allowed}/>
                    </div>
                </div>
            </div>
            <div className={"modal-baseline"}>
                <Button btnStyle={"grey"}
                    size={"small"}
                    onClick={() => handleClose()}
                    content={"Annuler"}/>

                {isSuccessAlbum ? (
                    <Button
                        btnStyle={"cancel-reverse"}
                        size={"small"}
                        onClick={() => setToggleAlert(true)}
                        content={"Supprimer la collection"}/>
                ) : ''}

                <Button onClick={() => handleSubmitAlbum()}
                    btnStyle={"primary-reverse"}
                    size={"small"}
                    disabled={currentAlbum.title.length < 3}
                    content={!!albumId ? 'Modifier' : 'Créer' }/>

                <Modal toggle={toggleAlert}
                       handleClose={() => setToggleAlert(false)}>
                    <Alert handleClose={() => setToggleAlert(false)}
                           handleDelete={() => mutateDeleteAlbum()}
                           text="Voulez-vous vraiment supprimer cette collection et tout son contenu ?"/>
                </Modal>
            </div>
        </div>
    )
};

export default AlbumInformations;